import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Icon } from '@components/icon/Icon';
import { useFormContext, useWatch } from 'react-hook-form';
import { type AdminRestaurant } from '../../team/apiHelpers';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import { YES_NO_SELECT_OPTIONS } from '../utils/generalSettingsFormHelpers';
import { type GeneralSettingsForm } from '../utils/generalSettingsFormTypes';
import { PACING_WINDOW_OPTIONS } from '../utils/pacingWindowOptions';
import layoutStyles from './PacingControlsSection.scss';

const PACING_CONTROLS_SECTION_EXPLANATION =
  'Set the pacing limit and window for your entire restaurant. This setting will make sure that at any given window, the number of guests arriving does not exceed the limit.';

export interface PacingControlsSectionProps {
  restaurant: AdminRestaurant;
  sectionTitleId: string;
  isSaving: boolean;
  onRequestRemovePacingControls: () => void;
}

export const PacingControlsSection = ({
  restaurant,
  sectionTitleId,
  isSaving,
  onRequestRemovePacingControls,
}: PacingControlsSectionProps) => {
  const { control } = useFormContext<GeneralSettingsForm>();
  const watchedPacingEnabled = useWatch({ control, name: 'pacingEnabled' });

  return (
    <section className={layoutStyles.section} aria-labelledby={sectionTitleId}>
      <SettingsOutletHeader
        variant="section"
        titleId={sectionTitleId}
        title="Pacing Controls"
        subtitle={PACING_CONTROLS_SECTION_EXPLANATION}
      />
      <div className={layoutStyles.formFields}>
        <ControlledFormSelect
          control={control}
          label="Use pacing controls"
          name="pacingEnabled"
          options={YES_NO_SELECT_OPTIONS}
          rules={{ required: true }}
          variant={LayoutVariant.Vertical}
          className={layoutStyles.constrainedField}
        />
        {watchedPacingEnabled === 1 ? (
          <>
            <ControlledFormInput
              control={control}
              label="Pacing limit"
              name="pacingLimit"
              rules={{
                min: {
                  message: 'Must be at least 1',
                  value: 1,
                },
                required: true,
              }}
              type="number"
              variant={LayoutVariant.Vertical}
              className={layoutStyles.constrainedField}
            />
            <ControlledFormSelect
              control={control}
              label="Pacing window"
              name="pacingWindow"
              options={PACING_WINDOW_OPTIONS}
              rules={{ required: true }}
              variant={LayoutVariant.Vertical}
              className={layoutStyles.constrainedField}
            />
          </>
        ) : null}
        {restaurant.pacingControls != null && watchedPacingEnabled === 1 ? (
          <button
            type="button"
            className={layoutStyles.removeButton}
            disabled={isSaving}
            aria-label="Remove pacing controls"
            onClick={onRequestRemovePacingControls}
          >
            <Icon ariaLabel="Remove pacing controls" name="trash" />
            <span className={layoutStyles.removeLabel}>
              Remove Pacing Controls
            </span>
          </button>
        ) : null}
      </div>
    </section>
  );
};
